(() => {
  'use strict';
  const createCheckout = async ({ variantId, page, t }) => {
    const country = page.dataset.country;
    const language = page.dataset.shopifyLocale.replace('-', '_').toUpperCase();
    if (!['DE','AT','PT','HU','CZ','PL','RO'].includes(country)) throw new Error(t('checkout.unavailable'));
    const merchandiseId = `gid://shopify/ProductVariant/${variantId}`;
    const query = 'mutation JblCheckout($input:CartInput!,$country:CountryCode!,$language:LanguageCode!) @inContext(country:$country,language:$language){cartCreate(input:$input){cart{checkoutUrl lines(first:5){nodes{quantity merchandise{... on ProductVariant{id}}}}}userErrors{field message}}}';
    const response = await fetch('/api/2026-10/graphql.json', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, variables: { country, language, input: { lines: [{ merchandiseId, quantity: 1 }], buyerIdentity: { countryCode: country } } } })
    });
    const result = await response.json();
    const payload = result.data?.cartCreate;
    const cart = payload?.cart;
    const lines = cart?.lines?.nodes;
    if (!response.ok || result.errors?.length || payload?.userErrors?.length || !cart?.checkoutUrl || lines?.length !== 1 || lines[0].quantity !== 1 || lines[0].merchandise.id !== merchandiseId) throw new Error(t('checkout.unavailable'));
    const destination = new URL(cart.checkoutUrl);
    if (destination.origin !== window.location.origin || destination.protocol !== 'https:') throw new Error(t('checkout.invalidUrl'));
    window.location.assign(destination.href);
  };
  const initialize = (page) => {
    if (page.dataset.initialized) return;
    page.dataset.initialized = 'true';
    const t = window.JblText.create(page);
    const locale = page.dataset.shopifyLocale || 'de';
    const query = (selector) => page.querySelector(selector);
    const all = (selector) => [...page.querySelectorAll(selector)];
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const parseData = (selector, fallback) => { try { return JSON.parse(query(selector)?.textContent || 'null') || fallback; } catch { return fallback; } };
    const colorGalleries = parseData('[data-color-galleries]', { colors: [] }).colors;
    const variantData = parseData('[data-variant-data]', { variants: [] });
    const colorButtons = all('[data-color-option]');
    colorGalleries.forEach((color) => {
      color.name = t(`colors.${color.key}`);
      color.images.forEach((image) => {
        const angle = /-(hero|front|back|left|top|bottom|strap|angle|boxcontents|packaging)(?:-|\.)/.exec(new URL(image.src, window.location.origin).pathname)?.[1] || 'hero';
        image.alt = t(`gallery.alt.${angle}`, { color: color.name });
      });
    });
    const variantForColor = (color) => variantData.variants.find((variant) => variant.key === color.key);
    const initialVariant = variantData.variants.find((variant) => String(variant.id) === String(page.dataset.variant));
    let selectedColor = colorGalleries.find((color) => color.key === initialVariant?.key) || colorGalleries[0];
    let selectedVariant = selectedColor ? variantForColor(selectedColor) : initialVariant;
    const galleryApi = window.JblGallery?.create(page, selectedColor?.images || []);

    const plus = query('[data-quantity-plus]');
    const toast = query('[data-quantity-toast]');
    let toastTimer;
    function positionToast() {
      if (!plus || !toast || !toast.classList.contains('is-visible')) return;
      const buttonRect = plus.getBoundingClientRect();
      const toastRect = toast.getBoundingClientRect();
      const viewport = window.visualViewport;
      const viewportLeft = viewport?.offsetLeft || 0;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportWidth = viewport?.width || window.innerWidth;
      const viewportHeight = viewport?.height || window.innerHeight;
      const desiredLeft = buttonRect.left + buttonRect.width / 2 - toastRect.width / 2;
      const left = Math.max(viewportLeft + 12, Math.min(desiredLeft, viewportLeft + viewportWidth - toastRect.width - 12));
      let top = buttonRect.top - toastRect.height - 10;
      if (top < viewportTop + 10) top = buttonRect.bottom + 10;
      top = Math.max(viewportTop + 10, Math.min(top, viewportTop + viewportHeight - toastRect.height - 10));
      toast.style.left = `${left}px`;
      toast.style.top = `${top}px`;
    }
    plus?.addEventListener('click', () => {
      clearTimeout(toastTimer);
      toast.textContent = t('quantity.toast');
      toast.classList.add('is-visible');
      positionToast();
      toastTimer = setTimeout(() => { toast.classList.remove('is-visible'); toast.textContent = ''; }, 3000);
    });
    window.addEventListener('resize', positionToast, { passive: true });
    window.addEventListener('scroll', positionToast, { passive: true });
    window.visualViewport?.addEventListener('resize', positionToast, { passive: true });
    window.visualViewport?.addEventListener('scroll', positionToast, { passive: true });

    const buyButtons = all('[data-buy]');
    let checkoutInProgress = false;
    const restoreBuyButtons = () => {
      buyButtons.forEach((button) => { button.disabled = !selectedVariant?.available; button.removeAttribute('aria-busy'); });
      colorButtons.forEach((button) => { button.disabled = false; });
    };
    const selectColor = (key, updateUrl = true) => {
      if (checkoutInProgress) return;
      const color = colorGalleries.find((item) => item.key === key);
      if (!color) return;
      const changed = selectedColor?.key !== color.key;
      selectedColor = color;
      selectedVariant = variantForColor(color);
      page.dataset.variant = selectedVariant?.id ? String(selectedVariant.id) : '';
      all('[data-color-name], [data-selected-color]').forEach((name) => { name.textContent = color.name; });
      colorButtons.forEach((button) => {
        const active = button.dataset.colorOption === key;
        button.setAttribute('aria-checked', String(active));
        button.tabIndex = active ? 0 : -1;
      });
      const stock = query('[data-variant-stock]');
      if (stock) { stock.textContent = selectedVariant?.available ? t('stock.available') : t('stock.unavailable'); stock.classList.toggle('is-unavailable', !selectedVariant?.available); }
      if (selectedVariant) {
        all('[data-current-price]').forEach((node) => { node.textContent = selectedVariant.price; });
        all('[data-current-compare]').forEach((node) => { node.textContent = selectedVariant.compare; });
      }
      restoreBuyButtons();
      if (changed) galleryApi?.setImages(color.images);
      if (updateUrl && selectedVariant?.id) { const url = new URL(window.location.href); url.searchParams.set('variant', selectedVariant.id); window.history.replaceState({}, '', url); }
      const error = query('[data-buy-error]');
      if (error) error.hidden = true;
    };
    colorButtons.forEach((button, index) => {
      button.addEventListener('click', () => selectColor(button.dataset.colorOption));
      button.addEventListener('keydown', (event) => {
        let next = index;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % colorButtons.length;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + colorButtons.length) % colorButtons.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = colorButtons.length - 1;
        else return;
        event.preventDefault(); selectColor(colorButtons[next].dataset.colorOption); colorButtons[next].focus({ preventScroll: true });
      });
    });
    if (selectedColor) selectColor(selectedColor.key, false);
    buyButtons.forEach((button) => button.addEventListener('click', async () => {
      if (checkoutInProgress || !page.dataset.variant) return;
      checkoutInProgress = true;
      colorButtons.forEach((item) => { item.disabled = true; });
      buyButtons.forEach((item) => { item.disabled = true; item.setAttribute('aria-busy', 'true'); });
      const error = query('[data-buy-error]');
      if (error) error.hidden = true;
      try {
        await createCheckout({ variantId: page.dataset.variant, page, t });
      } catch (failure) {
        checkoutInProgress = false;
        restoreBuyButtons();
        if (error) { error.hidden = false; error.textContent = t('checkout.error'); }
        console.error('JBL Checkout konnte nicht geöffnet werden.', failure);
      }
    }));
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        checkoutInProgress = false;
        restoreBuyButtons();
      }
    });

    const featureTrack = query('[data-feature-track]');
    const cards = all('.jbl-feature-card');
    const featureDots = all('[data-feature-dot]');
    let featureIndex = 0;
    function updateFeatureControls() {
      if (!featureTrack || !cards.length) return;
      const current = featureTrack.scrollLeft;
      const firstLeft = cards[0].offsetLeft;
      featureIndex = cards.reduce((best, card, index) => Math.abs(card.offsetLeft - firstLeft - current) < Math.abs(cards[best].offsetLeft - firstLeft - current) ? index : best, 0);
      const atEnd = current >= featureTrack.scrollWidth - featureTrack.clientWidth - 4;
      if (atEnd) featureIndex = cards.length - 1;
      query('[data-feature-prev]').disabled = current < 4;
      query('[data-feature-next]').disabled = atEnd;
      featureDots.forEach((dot, i) => { dot.classList.toggle('is-active', i === featureIndex); dot.setAttribute('aria-current', String(i === featureIndex)); });
    }
    function scrollFeature(index) {
      if (!featureTrack || !cards.length) return;
      const clamped = Math.max(0, Math.min(index, cards.length - 1));
      featureTrack.scrollTo({ left: cards[clamped].offsetLeft - cards[0].offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
    }
    query('[data-feature-prev]')?.addEventListener('click', () => scrollFeature(featureIndex - 1));
    query('[data-feature-next]')?.addEventListener('click', () => scrollFeature(featureIndex + 1));
    featureDots.forEach((dot, index) => dot.addEventListener('click', () => scrollFeature(index)));
    featureTrack?.addEventListener('scroll', updateFeatureControls, { passive: true });
    window.addEventListener('resize', updateFeatureControls, { passive: true });
    updateFeatureControls();
    all('[data-feature-expand]').forEach((button) => button.addEventListener('click', () => {
      const card = button.closest('.jbl-feature-card');
      const expanded = button.getAttribute('aria-expanded') !== 'true';
      card.classList.toggle('is-expanded', expanded);
      button.setAttribute('aria-expanded', String(expanded));
      button.textContent = expanded ? t('features.readLess') : t('features.readMore');
    }));

    const video = query('[data-jbl-video]');
    const videoShell = query('[data-video-shell]');
    query('[data-video-play]')?.addEventListener('click', async () => {
      if (!video) return;
      video.controls = true;
      videoShell.classList.add('is-playing');
      try { await video.play(); }
      catch (error) { videoShell.classList.remove('is-playing'); video.controls = true; }
    });
    video?.addEventListener('ended', () => { videoShell.classList.remove('is-playing'); video.controls = false; });

    if ('IntersectionObserver' in window) {
      const navLinks = all('.jbl-section-nav>a');
      const sectionNav = query('.jbl-section-nav');
      const navTargets = navLinks.map((link) => ({ link, target: query(link.hash) })).filter(({ target }) => target);
      let navFrame = 0;
      const updateActiveSection = () => {
        navFrame = 0;
        if (!navTargets.length) return;
        const anchorOffset = parseFloat(getComputedStyle(navTargets[0].target).scrollMarginTop) || 0;
        const navBottom = Math.max(0, sectionNav?.getBoundingClientRect().bottom || 0);
        const readingLine = Math.max(anchorOffset, Math.min(navBottom, window.innerHeight * 0.15) + 16) + 1;
        let active = navTargets[0];
        navTargets.forEach((item) => {
          if (item.target.getBoundingClientRect().top <= readingLine) active = item;
        });
        navLinks.forEach((link) => link.classList.toggle('is-active', link === active.link));
      };
      const scheduleActiveSection = () => {
        if (!navFrame) navFrame = window.requestAnimationFrame(updateActiveSection);
      };
      window.addEventListener('scroll', scheduleActiveSection, { passive: true });
      window.addEventListener('resize', scheduleActiveSection, { passive: true });
      window.addEventListener('load', scheduleActiveSection, { once: true });
      updateActiveSection();
      const mobileBuy = query('[data-mobile-buy]');
      const mobileHideTargets = [query('.jbl-purchase-panel .jbl-buy-button'), query('.jbl-quantity-row'), query('.jbl-video-shell'), query('.jbl-reviews'), query('.jbl-final-cta'), query('.jbl-footer')].filter(Boolean);
      const updateMobileBuy = () => {
        const viewportTop = window.visualViewport?.offsetTop || 0;
        const viewportBottom = viewportTop + (window.visualViewport?.height || window.innerHeight);
        const hasVisibleBlocker = mobileHideTargets.some((target) => {
          const rect = target.getBoundingClientRect();
          return rect.width > 0 && rect.height > 0 && rect.bottom > viewportTop && rect.top < viewportBottom;
        });
        const toastVisible = toast?.classList.contains('is-visible');
        mobileBuy?.classList.toggle('is-visible', !hasVisibleBlocker && !toastVisible);
      };
      const mobileObserver = new IntersectionObserver(updateMobileBuy, { threshold: 0 });
      mobileHideTargets.forEach((target) => mobileObserver.observe(target));
      window.addEventListener('scroll', updateMobileBuy, { passive: true });
      window.addEventListener('resize', updateMobileBuy, { passive: true });
      window.visualViewport?.addEventListener('resize', updateMobileBuy, { passive: true });
      window.visualViewport?.addEventListener('scroll', updateMobileBuy, { passive: true });
      if (toast) new MutationObserver(updateMobileBuy).observe(toast, { attributes: true, attributeFilter: ['class'] });
      updateMobileBuy();
    }

    const dataElement = query('[data-review-data]');
    const list = query('[data-review-list]');
    const results = query('[data-review-results]');
    const ratingSelect = query('[data-review-rating]');
    const search = query('[data-review-search]');
    const sort = query('[data-review-sort]');
    const more = query('[data-review-more]');
    const previous = query('[data-review-previous]');
    const pageLabel = query('[data-review-page]');
    const reset = query('[data-review-reset]');
    let reviewData = [];
    const pageSize = 8;
    let currentPage = 1;
    try {
      const parsed = JSON.parse(dataElement.textContent);
      reviewData = (Array.isArray(parsed) ? parsed : parsed.reviews || []).filter((review) => review && Number(review.rating) >= 2 && Number(review.rating) <= 5).map((review, index) => ({ ...review, rating: Number(review.rating), index }));
    } catch (error) {
      console.error('Bewertungen konnten nicht gelesen werden.', error);
    }
    const distributionLabel = query('[data-review-distribution-label]');
    if (distributionLabel) {
      const distributionTitle = t('reviews.distribution', {count: reviewData.length});
      distributionLabel.textContent = distributionTitle;
      distributionLabel.parentElement.setAttribute('aria-label', distributionTitle);
    }
    all('[data-star-filter]').forEach((button) => {
      const count = reviewData.filter((review) => review.rating === Number(button.dataset.starFilter)).length;
      const bar = button.querySelector('.jbl-rating-bar>i');
      if (bar) bar.style.width = `${reviewData.length ? count / reviewData.length * 100 : 0}%`;
      button.lastElementChild.textContent = String(count);
    });
    const create = (tag, className, text) => {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text !== undefined) element.textContent = text;
      return element;
    };
    const photoButton = (review, photo, photoIndex) => {
      const button = create('button', 'jbl-review-photo-button');
      button.type = 'button';
      button.dataset.reviewPhoto = '';
      button.dataset.reviewIndex = String(review.index);
      button.dataset.photoIndex = String(photoIndex);
      button.setAttribute('aria-label', t('photos.enlarge', {alt: photo.alt || t('photos.fallback')}));
      const image = create('img');
      const src = new URL(photo.src); src.searchParams.set('width', '240');
      image.src = src.href; image.alt = photo.alt || ''; image.width = photo.width; image.height = photo.height;
      image.loading = 'lazy'; image.decoding = 'async';
      button.append(image);
      return button;
    };
    const photoStrip = query('[data-review-photo-strip]');
    if (photoStrip) {
      const photos = document.createDocumentFragment(); let count = 0;
      reviewData.forEach((review) => (review.photos || []).forEach((photo, photoIndex) => { photos.append(photoButton(review, photo, photoIndex)); count += 1; }));
      photoStrip.replaceChildren(photos);
      const summary = query('[data-review-photo-summary]'); if (summary) summary.hidden = !count;
      const label = query('[data-review-photo-count]'); if (label) label.textContent = t('reviews.photosCount', {count});
    }
    function renderReviews() {
      if (!list) return;
      const term = search.value.toLocaleLowerCase(locale).trim();
      const rating = ratingSelect.value;
      let filtered = reviewData.filter((review) => (rating === 'all' || review.rating === Number(rating)) && `${review.author || ''} ${review.title_de || review.title || ''} ${review.text_de || review.text || ''}`.toLocaleLowerCase(locale).includes(term));
      if (sort.value === 'high') filtered.sort((a, b) => b.rating - a.rating || a.index - b.index);
      if (sort.value === 'low') filtered.sort((a, b) => a.rating - b.rating || a.index - b.index);
      const pageCount = Math.ceil(filtered.length / pageSize);
      currentPage = Math.max(1, Math.min(currentPage, Math.max(1, pageCount)));
      const pageStart = (currentPage - 1) * pageSize;
      const fragment = document.createDocumentFragment();
      filtered.slice(pageStart, pageStart + pageSize).forEach((review) => {
        const article = create('article', 'jbl-review');
        const author = create('div', 'jbl-review__author', review.author || t('reviews.anonymous'));
        const body = create('div', 'jbl-review__body');
        const meta = create('div', 'jbl-review__meta');
        const stars = create('span', 'jbl-stars', '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating));
        stars.setAttribute('aria-label', t('reviews.rating', {rating: review.rating}));
        const dateValue = review.date_de_display || review.date_display || '';
        meta.append(stars);
        if (dateValue) meta.append(create('span', 'jbl-review__date', dateValue));
        body.append(meta);
        const reviewTitle = review.title_de || review.title || '';
        if (reviewTitle) body.append(create('h3', 'jbl-review__title', reviewTitle));
        body.append(create('p', 'jbl-review__text', review.text_de || review.text || ''));
        if (review.photos?.length) {
          const photos = create('div', 'jbl-review__photos');
          review.photos.forEach((photo, photoIndex) => photos.append(photoButton(review, photo, photoIndex)));
          body.append(photos);
        }
        article.append(author, body);
        fragment.append(article);
      });
      if (!filtered.length) fragment.append(create('p', 'jbl-review__empty', reviewData.length ? t('reviews.empty') : t('reviews.unavailable')));
      list.replaceChildren(fragment);
      const rangeStart = filtered.length ? pageStart + 1 : 0;
      const rangeEnd = Math.min(pageStart + pageSize, filtered.length);
      results.textContent = t('reviews.results', {start:rangeStart, end:rangeEnd, count:filtered.length});
      pageLabel.textContent = t('reviews.page', {page:pageCount ? currentPage : 0, pages:pageCount});
      more.disabled = !pageCount || currentPage >= pageCount;
      previous.disabled = currentPage <= 1;
      more.setAttribute('aria-disabled', String(more.disabled));
      previous.setAttribute('aria-disabled', String(previous.disabled));
      reset.hidden = rating === 'all' && !term && sort.value === 'source';
      all('[data-star-filter]').forEach((button) => { const active = button.dataset.starFilter === rating; button.classList.toggle('is-active', active); button.setAttribute('aria-pressed', String(active)); });
    }
    const showReviewPage = (direction) => {
      currentPage += direction;
      renderReviews();
      results.focus({ preventScroll: true });
      results.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
    };
    search?.addEventListener('input', () => { currentPage = 1; renderReviews(); });
    ratingSelect?.addEventListener('change', () => { currentPage = 1; renderReviews(); });
    sort?.addEventListener('change', () => { currentPage = 1; renderReviews(); });
    more?.addEventListener('click', () => showReviewPage(1));
    previous?.addEventListener('click', () => showReviewPage(-1));
    reset?.addEventListener('click', () => { search.value = ''; ratingSelect.value = 'all'; sort.value = 'source'; currentPage = 1; renderReviews(); });
    all('[data-star-filter]').forEach((button) => button.addEventListener('click', () => { ratingSelect.value = ratingSelect.value === button.dataset.starFilter ? 'all' : button.dataset.starFilter; currentPage = 1; renderReviews(); }));
    renderReviews();
    window.JblReviewMedia?.create(page);
  };
  const start = () => document.querySelectorAll('[data-jbl-page]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', start);
})();
